import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, Sparkles, Undo2 } from "lucide-react";
import { autofillList, autofillUndo } from "../lib/api";
import { tr } from "../lib/i18n";

const KIND = { will: "Will", trust: "Trust", financial_poa: "Financial POA", healthcare_poa: "Healthcare POA", advance_directive: "Advance directive", auto_insurance: "Auto insurance", life_insurance: "Life insurance", health_insurance: "Health insurance", mortgage: "Mortgage", deed: "Deed", vehicle: "Vehicle papers", passport: "Passport", drivers_license: "Driver's license", id_card: "ID card" };

// Vault: what HearthVault filled in elsewhere from recent uploads (people, reminders, assets) — each one undoable.
export const AutofillCard = ({ readOnly }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["autofill"], queryFn: autofillList, staleTime: 30_000 });
  const [open, setOpen] = useState(false);
  const rows = (data?.updates || []).filter((u) => !u.undone).slice(0, 8);
  if (!rows.length) return null;
  const undo = async (id) => { try { qc.setQueryData(["autofill"], await autofillUndo(id)); toast.success(tr("Undone")); qc.invalidateQueries({ queryKey: ["estate-plan"] }); } catch (e) { toast.error(e?.response?.data?.detail || tr("Couldn't undo")); } };
  const changes = rows.reduce((n, u) => n + u.changes.length, 0);
  return (
    <section className="mb-6 rounded-2xl border border-border bg-card" data-testid="autofill-card">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left" data-testid="autofill-toggle">
        <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Sparkles className="h-4 w-4" /></span>
        <span className="flex-1 min-w-0"><span className="block font-medium text-sm">{tr("Updated from your uploads")}</span><span className="block text-xs text-muted-foreground">{changes} {tr(changes === 1 ? "change" : "changes")} · {tr("people, reminders and assets filled in for you")}</span></span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="border-t border-border divide-y divide-border" data-testid="autofill-list">
          {rows.map((u) => (
            <li key={u.id} className="p-4 flex items-start gap-3" data-testid={`autofill-row-${u.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground"><span className="font-mono uppercase tracking-wider text-[10px] mr-1.5 rounded-full border border-border px-1.5 py-0.5">{tr(KIND[u.kind] || u.kind)}</span>{u.doc_title}</div>
                <ul className="mt-1.5 space-y-0.5">{u.changes.map((c, i) => <li key={i} className="text-sm">{c.label}</li>)}</ul>
              </div>
              {!readOnly && <button type="button" onClick={() => undo(u.id)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive shrink-0" data-testid={`autofill-undo-${u.id}`}><Undo2 className="h-3.5 w-3.5" /> {tr("Undo")}</button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
